// src/edu-world/systems/audio-narrator.ts
// pt-PT audio narration for NPC dialogues (greeting, mission request, gratitude).
// Auto-initiates without requiring child to read.
// Handles missing/failed audio gracefully (console.warn, resolve immediately).
// Not wired into orchestrator yet — will be integrated in a later task.
// Requirements: 12.6

/**
 * Plays a narration audio file and resolves when playback finishes.
 * If the audio file fails to load or play, logs a warning and resolves immediately
 * so the game flow is never blocked by missing audio.
 *
 * @param audioPath - Path to the audio file (e.g., "audio/rosa-ola.mp3")
 * @returns Promise that resolves when playback completes or fails gracefully
 */
export function playNarration(audioPath: string): Promise<void> {
  return new Promise<void>((resolve) => {
    if (!audioPath) {
      console.warn('[EduWorld Audio] No audio path provided, skipping narration');
      resolve();
      return;
    }

    try {
      const audio = new Audio(audioPath);

      const cleanup = () => {
        audio.removeEventListener('ended', onEnded);
        audio.removeEventListener('error', onError);
      };

      const onEnded = () => {
        cleanup();
        resolve();
      };

      const onError = () => {
        cleanup();
        console.warn(`[EduWorld Audio] Failed to play narration: ${audioPath}`);
        resolve();
      };

      audio.addEventListener('ended', onEnded);
      audio.addEventListener('error', onError);

      // Attempt to play — may be blocked by browser autoplay policy
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          cleanup();
          console.warn(`[EduWorld Audio] Playback rejected (autoplay policy?): ${audioPath}`);
          resolve();
        });
      }
    } catch {
      console.warn(`[EduWorld Audio] Audio initialization failed: ${audioPath}`);
      resolve();
    }
  });
}

// ─── Internal state for stop control ───

let currentAudio: HTMLAudioElement | null = null;
let currentResolve: (() => void) | null = null;

/**
 * Plays a narration audio file with stop support.
 * Same graceful failure behavior as `playNarration`, but allows
 * interruption via `stopNarration()`.
 *
 * @param audioPath - Path to the audio file (e.g., "audio/rosa-ola.mp3")
 * @returns Promise that resolves when playback completes, is stopped, or fails gracefully
 */
export function playNarrationWithStop(audioPath: string): Promise<void> {
  // Stop any currently playing narration first
  stopNarration();

  return new Promise<void>((resolve) => {
    if (!audioPath) {
      console.warn('[EduWorld Audio] No audio path provided, skipping narration');
      resolve();
      return;
    }

    try {
      const audio = new Audio(audioPath);
      currentAudio = audio;
      currentResolve = resolve;

      const cleanup = () => {
        audio.removeEventListener('ended', onEnded);
        audio.removeEventListener('error', onError);
        if (currentAudio === audio) {
          currentAudio = null;
          currentResolve = null;
        }
      };

      const onEnded = () => {
        cleanup();
        resolve();
      };

      const onError = () => {
        cleanup();
        console.warn(`[EduWorld Audio] Failed to play narration: ${audioPath}`);
        resolve();
      };

      audio.addEventListener('ended', onEnded);
      audio.addEventListener('error', onError);

      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          cleanup();
          console.warn(`[EduWorld Audio] Playback rejected (autoplay policy?): ${audioPath}`);
          resolve();
        });
      }
    } catch {
      currentAudio = null;
      currentResolve = null;
      console.warn(`[EduWorld Audio] Audio initialization failed: ${audioPath}`);
      resolve();
    }
  });
}

/**
 * Stops any currently playing narration immediately.
 * Safe to call even when no narration is playing.
 */
export function stopNarration(): void {
  if (currentAudio) {
    try {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    } catch {
      // Silently ignore — audio element may be in a bad state
    }
    currentAudio = null;
  }
  if (currentResolve) {
    currentResolve();
    currentResolve = null;
  }
}
